import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, BookOpen, MessageCircle, Heart, Shield, Zap, Target, Award } from 'lucide-react';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function About() {
  return (
    <>
      {/* Hero */}
      <section className="page-hero">
        <HeroVideo className="page-hero-video" src={VIDEOS.meatRibs} />
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Our Story</span>
            <h1>About Fasting Carnivore</h1>
            <p>
              A community of people who found a simpler way to eat—and each other.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="heading-section">Our Mission</h2>
              <p className="about-mission-text">
                To help people reclaim their health through the power of ancestral eating and strategic fasting,
                supported by a community that understands the journey because they're living it too.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Origin Story */}
      <section className="section section-light">
        <div className="container">
          <div className="two-col about-origin-layout">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">How It Started</span>
              <h2 className="mb-lg">From Struggle to Solution</h2>
              <p className="about-body-text">
                It started with frustration. Years of counting calories, tracking macros, and fighting
                against our own bodies. Diets that promised results but delivered only hunger and failure.
              </p>
              <p className="about-body-text">
                Then came the discovery. What if the answer wasn't eating less, but eating differently?
                What if our ancestors had it right all along—simple, nutrient-dense food that satisfied
                completely?
              </p>
              <p className="about-body-text-last">
                Carnivore eating changed everything. And when we combined it with strategic fasting,
                the results were undeniable. Weight normalized. Energy soared. Chronic conditions improved
                or disappeared entirely.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="about-stats-card">
                <h3 className="about-stats-title">
                  The Numbers Don't Lie
                </h3>
                <div className="about-stats-grid">
                  <StatItem number="12+" label="Transformation stories documented" />
                  <StatItem number="4" label="Comprehensive books and guides" />
                  <StatItem number="24/7" label="Community support available" />
                  <StatItem number="100%" label="Satisfaction from our members" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Community Was Born */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">Community Was the Missing Piece</h2>
              <p className="about-community-text">
                We knew carnivore worked. But we also knew something else: doing it alone was hard.
                Family didn't understand. Friends thought we were crazy. Doctors were skeptical.
              </p>
              <p className="about-community-text">
                We needed to find our people. Others who got it. Others who'd experienced the same
                doubts, the same skepticism, and come out the other side transformed.
              </p>
              <p className="about-community-text">
                So we built a place where those people could find each other. Not a program with
                rules and restrictions. Not a course with modules and homework. A community.
                A tribe. A family.
              </p>
              <p className="about-community-closing">
                That's Fasting Carnivore. And that's why it works.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="section section-light">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-3xl"
          >
            <h2 className="heading-section">What We Believe</h2>
            <p className="about-belief-subtitle">
              These aren't just words on a page. They're the principles that guide everything we do.
            </p>
          </motion.div>

          <div className="grid-3 mx-auto-wider">
            <BeliefCard
              icon={<Target size={28} />}
              title="Simplicity Wins"
              description="Carnivore eating isn't complicated. Meat. Salt. Water. No tracking, no counting, no endless rules. Simple works."
            />
            <BeliefCard
              icon={<Users size={28} />}
              title="Community Matters"
              description="The difference between quitting and thriving isn't willpower—it's having people in your corner who understand."
            />
            <BeliefCard
              icon={<Heart size={28} />}
              title="You're Not Weird"
              description="You're not broken. You're not crazy. You just haven't found your people yet. We're here."
            />
            <BeliefCard
              icon={<Zap size={28} />}
              title="Results Over Theory"
              description="We care about what works in the real world, not what looks good in a study. Real people, real results."
            />
            <BeliefCard
              icon={<Shield size={28} />}
              title="No Judgment"
              description="Everyone's journey is different. We meet you where you are and support you where you want to go."
            />
            <BeliefCard
              icon={<Award size={28} />}
              title="Celebrate Everything"
              description="Every win matters. First day. First week. First pound. We celebrate it all because progress is progress."
            />
          </div>
        </div>
      </section>

      {/* What Members Get */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-3xl"
          >
            <h2 className="heading-section">What You Get as a Member</h2>
          </motion.div>

          <div className="grid-2 mx-auto-mid gap-xl">
            <MemberBenefit
              icon={<BookOpen size={24} />}
              title="Complete Book Library"
              description="All four books: Fasting Carnivore, Nano Fasting, Fasting Carnivore Kitchen, and Fasting Carnivore Performance. Plus all workbooks and guides."
            />
            <MemberBenefit
              icon={<MessageCircle size={24} />}
              title="Private Community"
              description="24/7 access to our Discord community. Ask questions, share wins, get support from people who understand your journey."
            />
            <MemberBenefit
              icon={<Users size={24} />}
              title="Real Support"
              description="This isn't a ghost town. It's an active community of people helping each other succeed. Someone's always there."
            />
            <MemberBenefit
              icon={<Zap size={24} />}
              title="Lifetime Access"
              description="Pay once, access forever. No subscriptions, no renewals, no surprises. All future updates included."
            />
          </div>
        </div>
      </section>

      {/* The Difference We Make */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="heading-section">The Difference We Make</h2>
              <p className="about-difference-text">
                We're not here to sell you supplements or meal plans. We're not here to tell you
                carnivore is the only way. We're here because we found something that works, and
                we want to share it with people who are ready.
              </p>
              <p className="about-difference-text">
                Our members report more energy, better sleep, clearer thinking, and most importantly—a
                sense that they've finally found their people. That feeling of "I'm not the only one"
                is worth everything.
              </p>
              <p className="about-difference-closing">
                If you're tired of fighting your body and ready to work with it instead, you've found
                the right place.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>You Found Your Tribe</h2>
            <p>
              We're glad you're here. Let's do this together.
            </p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join the Community — $97 Lifetime
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}

function StatItem({ number, label }: { number: string; label: string }) {
  return (
    <div className="about-stat-row">
      <span className="about-stat-number">
        {number}
      </span>
      <span className="about-stat-label">{label}</span>
    </div>
  );
}

function BeliefCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="about-belief-card"
    >
      <div className="about-belief-icon">{icon}</div>
      <h3 className="about-belief-title">{title}</h3>
      <p className="about-belief-desc">{description}</p>
    </motion.div>
  );
}

function MemberBenefit({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="about-benefit-card"
    >
      <div className="about-benefit-icon">
        {icon}
      </div>
      <div>
        <h3 className="about-benefit-title">{title}</h3>
        <p className="about-benefit-desc">{description}</p>
      </div>
    </motion.div>
  );
}
